import EmailIcon from '@mui/icons-material/MailOutlineRounded';
import { Stack, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import moment from 'moment';
import React, { memo, useMemo, useRef, useState } from 'react';
import { Dashboard, EmailBody } from '../../../../../types/files';
import ArrayUtils from '../../../../../utils/ArrayUtils';
import EmailsModal from '../../../../modals/tables/dashboards/rows/EmailsModal';
import useStyles from "../styles";
import classNames from 'classnames';

export const useLocalStyles = makeStyles((theme) => ({
    count: {
        display: '-webkit-box',
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 600,
        textDecoration: 'none',
        color: '#666666',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'normal',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
    },
    noCount: {
        fontSize: '0.85rem',
        fontWeight: 500,
        color: theme.colors.neutral['500'],
    },
    received: {
        display: '-webkit-box',
        fontFamily: 'Inter',
        fontSize: '0.85rem',
        fontWeight: 500,
        textDecoration: 'none',
        color: theme.colors.neutral['500'],
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'normal',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
    },
    label: {
        marginLeft: 8,
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 'bold',
        color: '#7bd4d4',
        "&:hover": {
            textDecoration: 'underline',
        }
    },
}));

const EmailsCell: React.FC<{ dashboard: Dashboard }> = ({ dashboard }) => {
    const classes = useStyles();
    const localClasses = useLocalStyles();
    const [comModalOpen, setComModalOpen] = useState<boolean>(false);
    const latestUpdateRef = useRef<HTMLElement>(null);

    const emailActivities = useMemo(() => {
        const activityItems: EmailBody[] = dashboard?.emailBodies
            ?.filter(emailBody => ['EMAIL', 'FULL_EMAIL'].includes(emailBody?.type || '')) as EmailBody[];

        return ArrayUtils.sortByDescending(activityItems ?? [], 'createdAt');
    }, [dashboard]);

    return (<>
        <Stack width="100%" ref={latestUpdateRef}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
                    <EmailIcon className={classes.icon} sx={{ fill: 'rgb(163, 167, 172)' }} />
                    <Stack direction="column" alignItems="flex-start">
                        <Typography className={classNames(localClasses.count, !emailActivities.length && localClasses.noCount)}
                            sx={!emailActivities.length ? { fontWeight: `500 !important` } : {}}>
                            {!emailActivities.length ? `None`
                                : `${emailActivities.length} email${emailActivities.length > 1 ? 's' : ''} received`}
                            {!!emailActivities.length && (
                                <Typography component="span" className={localClasses.label}
                                    onClick={() => setComModalOpen(true)}
                                    sx={{ cursor: 'pointer' }}
                                    display="inline">
                                    {'View'}
                                </Typography>
                            )}
                        </Typography>
                        {!!emailActivities.length && (
                            <Typography className={localClasses.received} >
                                {`Latest received ${moment(emailActivities[0]?.createdAt).fromNow()}`}
                            </Typography>
                        )}
                    </Stack>
                </Stack>
            </Stack>
        </Stack>
        {comModalOpen && (
            <EmailsModal open={comModalOpen}
                dashboard={dashboard}
                anchorElement={latestUpdateRef.current}
                onClose={() => setComModalOpen(false)} />
        )}
    </>);
}

export default memo(EmailsCell);
